import {Container, Point} from 'pixi.js';
import {SafeZone, TranslateConfig} from '@gongxigames/framework/core/fragments/display/SafeZone/SafeZone';
import type {Size2D} from '@gongxigames/framework/core/types';

export type FullScreenCoverOptions<T extends Container = Container> = {
    container: T;
    safeZone: SafeZone;
    contentSize: Size2D;
    align?: Point;
    offset?: Point;
};

export type FullScreenCoverUpdateOptions<T extends Container> = Partial<FullScreenCoverOptions<T>> & {
    safeZone: SafeZone;
};

/**
 * A helper object that will control the container
 * so that it always covers the entire screen.
 * The position and scale of the object will be automatically controlled.
 * The "resize" function must be called on screen resize.
 * Also there should be no containers with
 * modified scale and position between the object and the "safeZone".
 */
export class FullScreenCover<T extends Container = Container> {
    private tmpTranslateConfig: TranslateConfig = {
        position: new Point(),
        scale: 1,
    };

    container!: T;
    offset = new Point();
    align = new Point(0.5, 0.5);
    contentSize!: Size2D;

    constructor(options: FullScreenCoverOptions<T>) {
        this.update(options);
    }

    public update(options: FullScreenCoverUpdateOptions<T>): void {
        this.container = options.container ?? this.container;
        this.contentSize = options.contentSize ?? this.contentSize;
        this.align = options.align ?? this.align;
        this.offset = options.offset ?? this.offset;

        this.resize(options.safeZone);
    }

    public resize(safeZone: SafeZone): void {
        const {contentSize, tmpTranslateConfig, offset, align, container} = this;

        safeZone.calcInSafeFullScreenCoverBg(contentSize, tmpTranslateConfig, align);

        container.position.copyFrom(tmpTranslateConfig.position);
        container.scale.set(tmpTranslateConfig.scale);

        container.position.x += offset.x;
        container.position.y += offset.y;
    }
}